import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import format from 'string-template';
import * as Sentry from '@sentry/browser';
import { Tooltip } from '@mui/material';
import {
  AddCircleOutline as AssignIcon,
  DeleteOutline as RemoveIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';

import {
  HeaderRow,
  SubTitle,
  Title,
  TitleContainer,
} from '../../../../../../../components/v2/Header';
import { Card } from '../../../../../../../components/v2/Card';
import Table from '../../../../../../../components/v2/Table';
import UserContract from '../../../../../../../Model/UserContract';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../../../hooks/useSessionStore';
import { formatCurrencyCents } from '../../../../../../../utils/formatters';
import { formatPluralDuration } from '../../../../../../../utils/durationUnit';
import ExternalCoachContext, { withExternalCoachContextReady } from '../../../../../../context/ExternalCoachContext';
import useToast from '../../../../../../hooks/useToast';
import {
  AcceptUntilState,
  OfferingType,
  formatAcceptUntil,
  getAcceptUntilState,
  isChallengeStillAccepting,
} from '../../../../../../components/ContractModal/PackageCategoryCard/utils';
import ConfirmDialog from '../../../../../../components/ConfirmDialog';
import { getOfferingRows } from './utils';
import AssignOfferingModal from './AssignOfferingModal';
import {
  AcceptUntilText,
  ActionButton,
  ActionsCell,
  ClosedText,
  OfferingCell,
  OfferingRowClass,
  OfferingSubTitle,
  RemoveButton,
  TableContainer,
  UnassignedText,
} from './styles';
import texts from './texts.json';

const ROW_COUNT = 4;

/**
 * The coach's Inside Sales offerings: one row per category, with what is assigned to each.
 *
 * Categories are already stored on packages (`Package.category`); a challenge carries the same
 * field on its BASE contract. This section is the one place both are read, so nobody has to scroll
 * the product list looking for the `IS:` tags.
 */
const InsideSalesOfferings = ({ products, categorizedPackagesWithProduct, onOfferingsChanged }) => {
  const [categorizedChallenges, setCategorizedChallenges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [assigningRow, setAssigningRow] = useState(null);
  // The row whose offering the coach asked to remove, held while they confirm it.
  const [removingRow, setRemovingRow] = useState(null);

  const { coachDoc } = useContext(ExternalCoachContext);
  const { isAdmin, isOnCallUser } = useSessionStore();
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  const canEdit = isAdmin || isOnCallUser;
  const isEnabled = !!coachDoc?.isEnrolledInISProgram && !!coachDoc?.isACEnabled;

  const loadChallenges = useCallback(async () => {
    if (!isEnabled || !coachDoc?.id) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const contracts = await UserContract.getCategorizedChallengeContractsByCoach(coachDoc.id);
      if (isComponentMountedRef.current) {
        setCategorizedChallenges(contracts);
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { coachId: coachDoc?.id } });
      if (isComponentMountedRef.current) {
        showToast(texts.loadError, { error: true });
      }
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [coachDoc, isEnabled, isComponentMountedRef, showToast]);

  useEffect(() => {
    loadChallenges();
  }, [loadChallenges]);

  const rows = useMemo(() => getOfferingRows({
    categorizedPackages: categorizedPackagesWithProduct,
    categorizedChallenges,
  }), [categorizedPackagesWithProduct, categorizedChallenges]);

  const handleAssigned = useCallback(async () => {
    setAssigningRow(null);
    await Promise.all([loadChallenges(), onOfferingsChanged()]);
  }, [loadChallenges, onOfferingsChanged]);

  const handleRemove = useCallback(async () => {
    const row = removingRow;
    setRemovingRow(null);
    if (!row?.offering) {
      return;
    }
    setIsLoading(true);
    try {
      await row.offering.doc.setCategory(null);
      showToast(texts.removed, { type: 'success' });
      await Promise.all([loadChallenges(), onOfferingsChanged()]);
    } catch (error) {
      Sentry.captureException(error, { extra: { category: row.category, coachId: coachDoc?.id } });
      if (isComponentMountedRef.current) {
        showToast(texts.removeError, { error: true });
      }
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [removingRow, loadChallenges, onOfferingsChanged, showToast, isComponentMountedRef, coachDoc]);

  // Shade the row the way the lead modal shades the card, so both read the same at a glance.
  const getRowClassName = useCallback(({ row: { offering } }) => {
    if (offering?.type !== OfferingType.CHALLENGE) {
      return '';
    }
    const state = getAcceptUntilState(offering.doc.acceptUntil);
    if (state === AcceptUntilState.CLOSED) {
      return OfferingRowClass.CLOSED;
    }
    return state === AcceptUntilState.CLOSING_SOON ? OfferingRowClass.CLOSING_SOON : '';
  }, []);

  const renderPrice = useCallback(({ row: { offering } }) => {
    if (!offering) {
      return texts.empty;
    }
    const { doc, type } = offering;
    const price = formatCurrencyCents(doc.initialPaymentInCents, doc.currency, { minimumFractionDigits: 2 });

    if (type === OfferingType.CHALLENGE) {
      return `${price} ${texts.oneTime}`;
    }
    return price;
  }, []);

  const renderTerm = useCallback(({ row: { offering } }) => {
    if (!offering) {
      return texts.empty;
    }
    const { doc, type } = offering;

    if (type === OfferingType.CHALLENGE) {
      const duration = formatPluralDuration(doc.initialTerm, doc.durationUnit);
      const isAccepting = isChallengeStillAccepting(doc);
      const acceptUntil = formatAcceptUntil(doc.acceptUntil);

      return (
        <OfferingCell>
          <span>{duration}</span>
          {!!acceptUntil && isAccepting && (
            <AcceptUntilText>{format(texts.acceptingUntil, { date: acceptUntil })}</AcceptUntilText>
          )}
          {!!acceptUntil && !isAccepting && <ClosedText>{texts.enrollmentClosed}</ClosedText>}
        </OfferingCell>
      );
    }

    const term = doc.initialTerm ? formatPluralDuration(doc.initialTerm) : texts.empty;
    if (!doc.monthlyPaymentInCents) {
      return term;
    }
    const monthly = format(texts.monthlyAfterInitial, {
      amount: formatCurrencyCents(doc.monthlyPaymentInCents, doc.currency, { minimumFractionDigits: 2 }),
    });
    return format(texts.termWithMonthly, { term, monthly });
  }, []);

  if (!isEnabled) {
    return null;
  }

  return (
    <Card>
      <HeaderRow>
        <TitleContainer>
          <Title>{texts.title}</Title>
          <SubTitle>{texts.subTitle}</SubTitle>
        </TitleContainer>
      </HeaderRow>
      <TableContainer>
        <Table
          autoHeight
          enableSearch={false}
          getRowClassName={getRowClassName}
          loading={isLoading}
          pageSize={ROW_COUNT}
          rows={rows}
          columns={[
            {
              field: 'category',
              headerName: texts.headers.category,
              flex: 4,
              minWidth: 110,
              sortable: false,
              renderCell: ({ row: { Tag, label } }) => <Tag>{label}</Tag>,
            },
            {
              field: 'offering',
              headerName: texts.headers.offering,
              flex: 8,
              minWidth: 150,
              sortable: false,
              renderCell: ({ row: { offering } }) => (offering ? (
                <OfferingCell>
                  <span>{offering.name}</span>
                  {!!offering.subTitle && <OfferingSubTitle>{offering.subTitle}</OfferingSubTitle>}
                </OfferingCell>
              ) : <UnassignedText>{texts.notAssigned}</UnassignedText>),
            },
            {
              field: 'price',
              headerName: texts.headers.price,
              flex: 5,
              minWidth: 110,
              sortable: false,
              renderCell: renderPrice,
            },
            {
              field: 'term',
              headerName: texts.headers.term,
              flex: 7,
              minWidth: 150,
              sortable: false,
              renderCell: renderTerm,
            },
            {
              field: 'actions',
              headerName: texts.headers.actions,
              flex: 3,
              minWidth: 84,
              sortable: false,
              align: 'right',
              headerAlign: 'right',
              renderCell: ({ row }) => (
                <ActionsCell>
                  <Tooltip title={row.offering ? texts.updateTooltip : texts.assignTooltip}>
                    <span>
                      <ActionButton
                        size="small"
                        disabled={!canEdit}
                        aria-label={row.offering ? texts.update : texts.assign}
                        onClick={() => setAssigningRow(row)}
                      >
                        {row.offering ? <EditIcon fontSize="small" /> : <AssignIcon fontSize="small" />}
                      </ActionButton>
                    </span>
                  </Tooltip>
                  {!!row.offering && (
                    <Tooltip title={texts.removeTooltip}>
                      <span>
                        <RemoveButton
                          size="small"
                          disabled={!canEdit}
                          aria-label={texts.remove}
                          onClick={() => setRemovingRow(row)}
                        >
                          <RemoveIcon fontSize="small" />
                        </RemoveButton>
                      </span>
                    </Tooltip>
                  )}
                </ActionsCell>
              ),
            },
          ]}
        />
      </TableContainer>
      <ConfirmDialog
        isOpen={!!removingRow}
        onConfirm={handleRemove}
        onCancel={() => setRemovingRow(null)}
        dialogTexts={{
          title: texts.removeConfirm.title,
          content: format(texts.removeConfirm.content, {
            offering: removingRow?.offering?.name ?? '',
            category: removingRow?.label ?? '',
          }),
          confirm: texts.removeConfirm.confirm,
        }}
      />
      {!!assigningRow && (
        <AssignOfferingModal
          open
          row={assigningRow}
          products={products}
          coachId={coachDoc.id}
          onClose={() => setAssigningRow(null)}
          onAssigned={handleAssigned}
        />
      )}
    </Card>
  );
};

InsideSalesOfferings.propTypes = {
  products: PropTypes.array.isRequired,
  categorizedPackagesWithProduct: PropTypes.array.isRequired,
  onOfferingsChanged: PropTypes.func.isRequired,
};

export default compose(
  withExternalCoachContextReady,
  observer,
)(InsideSalesOfferings);
